import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

/**
 * Rent invoice data model
 */
export const RentInvoice = {
  id: "",
  tenantId: "",
  propertyId: "",
  month: "", // YYYY-MM format
  dueDate: null,
  amount: 0,
  status: "due", // 'due', 'paid', 'late', 'overdue'
  paidDate: null,
  lateFeeAmount: 0,
  lateFeeApplied: false,
  reminderSent: false,
  reminderSentDate: null,
  notes: "",
  createdAt: null,
  updatedAt: null,
};

/**
 * Get rent overview for a specific month
 */
export async function getRentOverview({ month }) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    console.log(
      "getRentOverview: Loading data for month:",
      month,
      "user:",
      user.uid
    );

    // Load real rent data from Firebase
    const rentRef = collection(db, "rentInvoices");
    const q = query(
      rentRef,
      where("landlordId", "==", user.uid),
      where("month", "==", month)
    );
    const querySnapshot = await getDocs(q);

    const invoices = [];
    querySnapshot.forEach((doc) => {
      invoices.push({ id: doc.id, ...doc.data() });
    });

    console.log("getRentOverview: Found invoices:", {
      count: invoices.length,
      invoices: invoices.map((inv) => ({
        id: inv.id,
        status: inv.status,
        amount: inv.amount,
        tenantId: inv.tenantId,
      })),
    });

    // Calculate real statistics
    const overview = {
      due: invoices.filter((inv) => inv.status === "due").length,
      paid: invoices.filter((inv) => inv.status === "paid").length,
      late: invoices.filter((inv) => inv.status === "late").length,
      total: invoices.length,
      month,
      invoices: invoices,
    };

    console.log("getRentOverview: Calculated overview:", overview);

    return overview;
  } catch (error) {
    console.error("Error getting rent overview:", error);
    throw error;
  }
}

/**
 * Send rent reminder to a tenant
 * @param {string} tenantId - Tenant ID
 * @param {string} [paymentId] - Optional payment ID to send reminder for specific payment
 */
export async function sendReminder(tenantId, paymentId = null) {
  console.log(
    "[DEBUG] sendReminder called with tenantId:",
    tenantId,
    "paymentId:",
    paymentId
  );

  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.log("[DEBUG] User not authenticated");
      throw new Error("User not authenticated");
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      throw new Error("API URL not configured");
    }

    console.log(
      "[DEBUG] Making API call to",
      `${apiUrl}/rent/send-reminder/send`
    );

    // Build request body
    const requestBody = {
      tenantId,
      landlordId: user.uid,
      reminderType: "rent_due",
      deliveryMethods: ["email"],
    };

    // Add paymentId if provided
    if (paymentId) {
      requestBody.paymentId = paymentId;
    }

    // Make API call to backend
    const response = await fetch(`${apiUrl}/rent/send-reminder/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify(requestBody),
    });

    console.log("[DEBUG] API response status:", response.status);
    console.log("[DEBUG] API response ok:", response.ok);

    if (!response.ok) {
      const errorData = await response.json();
      console.log("[DEBUG] API error response:", errorData);

      // Handle specific error cases
      if (
        errorData.message?.includes("payment not found") ||
        errorData.message?.includes("Payment not found")
      ) {
        throw new Error("Payment not found or does not belong to this tenant");
      }

      throw new Error(errorData.message || "Failed to send reminder");
    }

    const result = await response.json();
    console.log("[DEBUG] API success response:", result);
    return result;
  } catch (error) {
    console.error("[DEBUG] Error in sendReminder:", error);
    throw error;
  }
}

/**
 * Apply late fee to a tenant
 */
export async function applyLateFee(tenantId, amount) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Apply late fee to tenant in Firebase
    const lateFeeRef = doc(collection(db, "lateFees"));
    const lateFeeData = {
      tenantId,
      landlordId: user.uid,
      amount,
      appliedDate: new Date(),
      status: "applied",
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(lateFeeRef, lateFeeData);

    return {
      ok: true,
      lateFeeId: lateFeeRef.id,
      message: "Late fee applied successfully",
    };
  } catch (error) {
    console.error("Error applying late fee:", error);
    throw error;
  }
}

/**
 * Get rent invoices for a tenant
 */
export async function getTenantInvoices(tenantId, { month } = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // TODO(api): implement server call
    // For now, return mock data
    return {
      invoices: [
        {
          id: "inv_1",
          tenantId,
          propertyId: "prop_1",
          month: "2024-01",
          dueDate: new Date("2024-01-01"),
          amount: 1800,
          status: "paid",
          paidDate: new Date("2024-01-01"),
          lateFeeAmount: 0,
          lateFeeApplied: false,
          reminderSent: false,
          reminderSentDate: null,
          notes: "",
          createdAt: new Date("2024-01-01"),
          updatedAt: new Date("2024-01-01"),
        },
        {
          id: "inv_2",
          tenantId,
          propertyId: "prop_1",
          month: "2023-12",
          dueDate: new Date("2023-12-01"),
          amount: 1800,
          status: "paid",
          paidDate: new Date("2023-12-01"),
          lateFeeAmount: 0,
          lateFeeApplied: false,
          reminderSent: false,
          reminderSentDate: null,
          notes: "",
          createdAt: new Date("2023-12-01"),
          updatedAt: new Date("2023-12-01"),
        },
      ],
      total: 2,
    };
  } catch (error) {
    console.error("Error getting tenant invoices:", error);
    throw error;
  }
}

/**
 * Create a new rent invoice
 */
export async function createRentInvoice(invoiceData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Create rent invoice in Firebase
    const invoiceRef = doc(collection(db, "rentInvoices"));
    const newInvoice = {
      id: invoiceRef.id,
      ...invoiceData,
      landlordId: user.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(invoiceRef, newInvoice);

    return {
      success: true,
      invoice: newInvoice,
      message: "Rent invoice created successfully",
    };
  } catch (error) {
    console.error("Error creating rent invoice:", error);
    throw error;
  }
}

/**
 * Update rent invoice status
 */
export async function updateInvoiceStatus(invoiceId, status) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Update invoice status in Firebase
    const invoiceRef = doc(db, "rentInvoices", invoiceId);
    await updateDoc(invoiceRef, {
      status,
      updatedAt: new Date(),
    });

    // Get updated invoice
    const invoiceSnap = await getDoc(invoiceRef);
    const updatedInvoice = {
      id: invoiceSnap.id,
      ...invoiceSnap.data(),
    };

    return {
      success: true,
      invoice: updatedInvoice,
      message: "Invoice status updated successfully",
    };
  } catch (error) {
    console.error("Error updating invoice status:", error);
    throw error;
  }
}
